import React from 'react';
import ReactDOM from 'react-dom';
import dragonfly from 'dragonfly-v3';
import { IntlProvider } from 'react-intl';

import BaseHandler from './BaseHandler';
import Api from '../../apis/Api';
import InfoBubbleParcel from '../../components/infoBubble/InfoBubbleParcel';
import messages, { locale } from '../../languages/languageConfig';
import interactiveFeatureLayerIds from '../interactiveFeatureLayerIds';
import {
    ESRI_PARCELS_LAYER_ID,
    ESRI_PARCELS_MIN_ZOOM,
    isEsriParcelLookupEnabled,
} from '../../helpers/esriParcels';
import { createLatestRequestGuard, parcelPopupModel } from '../../helpers/parcelPopup';

// A click on the visible parcel layer asks Rails for the parcel under it
// (GET /api/v1/parcels/lookup). Raster tiles carry no features, so this cannot go
// through InteractivityHandler, which also only sees clicks in CLICK bubble mode.
class ParcelsHandler extends BaseHandler {
    constructor(mapViewer) {
        super(mapViewer);
        this._data = mapViewer.dragonflyMapData;
        this._guard = createLatestRequestGuard();
        this._popup = new dragonfly.Popup({ closeOnClick: false, closeButton: true });
        // A popup the user closed must not come back when a slow answer lands.
        this._popup.on('close', () => this._guard.cancel());
        this._clickStartedInPick = false;

        this.setGluBusEvents({
            MAP_LIBRARY_LAYERS_UPDATED: this.onLibraryLayersUpdated,
        });
    }

    bind() {
        super.bind();
        if (isEsriParcelLookupEnabled()) {
            this.map.on('mousedown', this.onMapMouseDown);
            this.map.on('click', this.onMapClick);
        }
    }

    unbind() {
        this.map.off('mousedown', this.onMapMouseDown);
        this.map.off('click', this.onMapClick);
        super.unbind();
    }

    remove() {
        this._guard.cancel();
        this._popup.remove();
    }

    get isParcelLayerVisible() {
        return (
            !!this.map.getLayer(ESRI_PARCELS_LAYER_ID) &&
            this.map.getLayoutProperty(ESRI_PARCELS_LAYER_ID, 'visibility') !== 'none'
        );
    }

    onLibraryLayersUpdated = () => {
        if (!this.isParcelLayerVisible) this.remove();
    };

    // A location pick resolves on mouseup, before the click fires, so whether this
    // click belongs to a pick is decided when it starts.
    onMapMouseDown = () => {
        this._clickStartedInPick = !!this.mapViewer.isPickingLocation;
    };

    onMapClick = e => {
        // Mask drawing and the export preview switch feature interactivity off.
        if (!this.mapViewer.isFeatureInteractivityEnabled || this._clickStartedInPick) return;
        if (!this.isParcelLayerVisible || this.map.getZoom() < ESRI_PARCELS_MIN_ZOOM) return;
        if (this._hitsInteractiveFeature(e.point)) return;

        const { lng, lat } = e.lngLat;
        const token = this._guard.next();
        this._show([lng, lat], { kind: 'loading' });

        const showIfCurrent = model => {
            if (this._guard.isCurrent(token)) this._show([lng, lat], model);
        };
        Api.olap.parcelLookup({ query: { lat, lng } }).then(
            response => showIfCurrent(parcelPopupModel(response.body)),
            () => showIfCurrent({ kind: 'error' }),
        );
    };

    _hitsInteractiveFeature(point) {
        const layers = interactiveFeatureLayerIds(this._data, this.mapInstance).filter(id =>
            this.map.getLayer(id),
        );
        return layers.length > 0 && this.map.queryRenderedFeatures(point, { layers }).length > 0;
    }

    _show(lngLat, model) {
        const node = document.createElement('div');
        ReactDOM.render(
            <IntlProvider locale={locale} messages={messages}>
                <InfoBubbleParcel model={model} />
            </IntlProvider>,
            node,
        );
        this._popup.setLngLat(lngLat).setDOMContent(node).addTo(this.map);
        this._popup._container.classList.add('layers-popup-container');
    }
}

export default ParcelsHandler;
